import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Grid,
  Chip,
  DialogActions,
} from "@mui/material";
import Papa from "papaparse";
import { toast } from "react-toastify";
import EmailsApi from "../../services/api/emails";
import TierDropdown from "../parts/tierDropdown";

const parserOptions = {
  header: true,
  dynamicTyping: true,
  skipEmptyLines: true,
  transformHeader: (header) => header.toLowerCase().replace(/\W/g, "_"),
};

function EmailListImport(props) {
  const { token, tiers = [], simpleTiers = [], onClose } = props;
  const [emails, setEmails] = useState([]);
  // Map tier name to tier object for lookup
  const tierMapByName = React.useMemo(() => {
    const map = {};
    tiers.forEach((tier) => {
      map[tier.nickname] = tier;
    });
    return map;
  }, [tiers]);

  // Use full tier objects for dropdown
  const tierData = tiers;
  const [template, setTemplate] = useState("1");
  const [isLoading, setIsLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const fileInputRef = useRef(null);

  let emailsToSend = 0;

  const sendEmails = async () => {
    setSendingEmails(true);
    setIsLoading(true);

    let formatted = [];

    emails.forEach((items) => {
      const tierId = items["tierId"];
      items.forEach((item) => {
        if (!item[1]) item[1] = "No note";
        formatted.push({
          email: item[0],
          tier: tierId,
          note: item[1],
        });
      });
    });

    const authToken = await token();
    const emailData = {
      emails: formatted,
      bulk: true,
    };

    try {
      const response = await EmailsApi.addEmail(authToken, emailData);
      
      if (response.success) {
        const results = response.data?.results;
        if (results) {
          const successful = results.filter(r => r.success).length;
          const failed = results.filter(r => !r.success);
          
          if (failed.length > 0) {
            const duplicates = failed.filter(r => r.message === 'Email already exists').length;
            const errors = failed.length - duplicates;
            
            let message = `${successful} emails imported successfully.`;
            if (duplicates > 0) message += ` ${duplicates} emails already existed.`;
            if (errors > 0) message += ` ${errors} emails failed due to errors.`;
            
            toast.success(message);
          } else {
            toast.success(`All ${successful} emails imported successfully!`);
          }
        } else {
          toast.success("Import completed successfully");
        }
        onClose();
      } else {
        toast.error(`Error: Failed to import emails - ${response.message}`);
      }
    } catch (err) {
      toast.error(`Error: ${err.message || err}: Failed to import emails`);
    } finally {
      setIsLoading(false);
      setSendingEmails(false);
    }
  };

  const setTier = (color, id, name, tierId, index) => {
    let updatedEmails = [...emails];
    updatedEmails[index]["found"] = "true";
    updatedEmails[index]["tierId"] = id;
    setEmails(updatedEmails);
  };

  const readFile = (file) => {
    try {
      Papa.parse(file, {
        ...parserOptions,
        complete: (results) => {
          const data = results.data.map((row) => ({ data: row }));

          let parsedEmails = [];
          let temp_emails = {};
          let temp_tiers = [];
          let tiersCounter = -1;
          emailsToSend = 0;

          data.forEach(({ data: item }) => {
            if (template === "1") { // Patreon
              if (!item.email) {
                tiersCounter++;
                parsedEmails[tiersCounter] = [];
                const tierName = item.firstname;
                parsedEmails[tiersCounter]["tier"] = tierName;
                const tierObj = tierMapByName[tierName];
                if (tierObj) {
                  parsedEmails[tiersCounter]["found"] = "true";
                  parsedEmails[tiersCounter]["tierId"] = tierObj.relId;
                } else {
                  parsedEmails[tiersCounter]["found"] = "false";
                  parsedEmails[tiersCounter]["tierId"] = tierData[0]?.relId || 0;
                }
              } else {
                emailsToSend++;
                parsedEmails[tiersCounter].push([item.email, item.status]);
              }
            } else if (template === "2") { // ShieldShot
              emailsToSend++;
              const tierName = item.tier;
              if (!temp_emails[tierName]) temp_emails[tierName] = [];
              temp_emails[tierName].push([item.email, item.note, tierName]);
              if (!temp_tiers.includes(tierName)) {
                temp_tiers.push(tierName);
              }
            }
          });

          if (template === "2") {
            temp_tiers.forEach((tier, index) => {
              parsedEmails[index] = [];
              temp_emails[tier].forEach((item) => {
                parsedEmails[index]["tier"] = item[2];
                if (simpleTiers.includes(item[2])) {
                  parsedEmails[index]["found"] = "true";
                  parsedEmails[index]["tierId"] = simpleTiers.indexOf(item[2]);
                } else {
                  parsedEmails[index]["found"] = "false";
                  parsedEmails[index]["tierId"] = 0;
                }
                parsedEmails[index].push([item[0], item[1]]);
              });
            });
          }

          if (!parsedEmails.length) {
            setMsg("There doesn't seem to be any emails found in this file...");
          }
          setEmails(parsedEmails);
          setIsLoading(false);
          setShowResult(true);
        },
        error: (err) => {
          toast.warn("Looks like something went wrong with reading your CSV file. Please check your file and try again");
        },
      });
    } catch (err) {
      toast.warn("Looks like something went wrong with reading your CSV file. Please check your file and try again");
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setIsLoading(true);
      setShowResult(false);
      setMsg("");
      readFile(e.target.files[0]);
    }
  };

  return (
    <React.Fragment>
      <Box p={1}>
        <FormControl fullWidth margin="normal">
          <InputLabel id="template-select-label">Template</InputLabel>
          <Select
            labelId="template-select-label"
            value={template}
            label="Template"
            onChange={(e) => setTemplate(e.target.value)}
          >
            <MenuItem value="1">Patreon</MenuItem>
            <MenuItem value="2">ShieldShot</MenuItem>
          </Select>
        </FormControl>

        <input
          type="file"
          ref={fileInputRef}
          accept=".csv"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <Button
          variant="outlined"
          fullWidth
          onClick={() => fileInputRef.current.click()}
          sx={{ mt: 1, mb: 1 }}
        >
          Select a file
        </Button>
        <Typography variant="caption" display="block">
          ShieldShot segregates email lists by the "firstname" in Patreon CSV files.
        </Typography>

        {showResult && (
          <Box sx={{ mt: 2, borderTop: 1, borderColor: "divider", pt: 2 }}>
            {msg && <Typography color="error">{msg}</Typography>}

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <React.Fragment>
                {emails.map((items, index) => (
                  <Box sx={{ my: 2 }} key={index}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs>
                        <Typography variant="h6">{items["tier"]}</Typography>
                        <Chip
                          label={items["found"] === "true" ? "Matching tier found" : "No Matching Tier"}
                          color={items["found"] === "true" ? "success" : "error"}
                          size="small"
                        />
                      </Grid>
                      <Grid item>
                        <TierDropdown
                          defaultValue={items["tierId"]}
                          token={token}
                          action={setTier}
                          size="small"
                          tierData={tierData}
                          extraData={index}
                        />
                      </Grid>
                    </Grid>

                    <List dense>
                      {items.map((item, idx) => (
                        <ListItem key={idx}>
                          <ListItemText primary={item[0]} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </React.Fragment>
            )}
          </Box>
        )}
      </Box>

      {showResult && !isLoading && (
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={sendEmails}
            disabled={sendingEmails}
          >
            {sendingEmails ? <CircularProgress size={24} /> : "Import Emails"}
          </Button>
        </DialogActions>
      )}
    </React.Fragment>
  );
}

export default EmailListImport;
